import type React from 'react';
import type { IntlShape } from 'react-intl';
import {
  MdAutoAwesome,
  MdCode,
  MdLayers,
  MdOutlineArchitecture,
  MdOutlineCloud,
  MdOutlineComputer,
  MdOutlineViewInAr,
} from 'react-icons/md';
import { SiAutodesk, SiBlender, SiDassaultsystemes, SiFreecad, SiRhinoceros } from 'react-icons/si';
import type { AcquisitionType, EntitlementResponse, RunnerMode } from '@api/types/entitlements';
import AibuildLogoIcon from './AibuildLogoIcon';

export type SkillIcon = React.ComponentType<{ size?: number }>;

interface Message {
  readonly id: string;
  readonly defaultMessage: string;
}

const SKILL_ICONS: Record<string, SkillIcon> = {
  AIBUILD_CAM: AibuildLogoIcon,
  SUPERFETS: AibuildLogoIcon,
};

const TOOL_ICONS: Record<string, SkillIcon> = {
  blender: SiBlender,
  freecad: SiFreecad,
  fusion: SiAutodesk,
  catia: SiDassaultsystemes,
  solidworks: SiDassaultsystemes,
  rhino_desktop: SiRhinoceros,
  rhino_compute: SiRhinoceros,
};

const CATEGORY_ICONS: Record<string, SkillIcon> = {
  CAD: MdOutlineViewInAr,
  CAE: MdOutlineArchitecture,
  CAM: MdLayers,
  AI: MdAutoAwesome,
  Coding: MdCode,
};

export const skillIcon = (skill: EntitlementResponse): SkillIcon =>
  SKILL_ICONS[skill.skillId] ??
  skill.skillKeys.map((key) => TOOL_ICONS[key.toLowerCase()]).find(Boolean) ??
  CATEGORY_ICONS[skill.category] ??
  MdLayers;

const CATEGORY_LABELS: Record<string, Message> = {
  CAD: { id: 'marketplace.category.cad', defaultMessage: 'CAD' },
  CAE: { id: 'marketplace.category.cae', defaultMessage: 'Simulation' },
  CAM: { id: 'marketplace.category.cam', defaultMessage: 'CAM' },
  AI: { id: 'marketplace.category.ai', defaultMessage: 'AI' },
  Coding: { id: 'marketplace.category.coding', defaultMessage: 'Coding' },
};

export const categoryLabel = (intl: IntlShape, category: string): string => {
  const label = CATEGORY_LABELS[category];
  return label ? intl.formatMessage(label) : category;
};

export const RUNNER_ICONS: Record<RunnerMode, SkillIcon> = {
  cloud: MdOutlineCloud,
  local: MdOutlineComputer,
};

export const RUNNER_LABELS: Record<RunnerMode, Message> = {
  cloud: { id: 'marketplace.runner.cloud', defaultMessage: 'Cloud' },
  local: { id: 'marketplace.runner.local', defaultMessage: 'Local' },
};

const DEFAULT_CONTACT_SALES_URL = 'https://ai-build.com/contact-us/general-enquiries/';

const CONTACT_SALES_URLS: Record<string, string> = {
  SUPERFETS: 'https://ai-build.com/contact-us/fets-contact-form/',
};

export type SkillAction =
  | { readonly kind: 'download' }
  | { readonly kind: 'contactSales'; readonly url: string }
  | { readonly kind: 'none' };

export const skillAction = (skill: EntitlementResponse): SkillAction => {
  if (skill.enabled) return skill.infoText ? { kind: 'download' } : { kind: 'none' };
  if (skill.acquisitionType === 'CONTACT_SALES') {
    return {
      kind: 'contactSales',
      url: CONTACT_SALES_URLS[skill.skillId] ?? DEFAULT_CONTACT_SALES_URL,
    };
  }
  return { kind: 'none' };
};

const ACQUISITION_LABELS: Record<AcquisitionType, Message> = {
  CONTACT_SALES: { id: 'marketplace.action.contactSales', defaultMessage: 'Contact Sales' },
  STRIPE_CHECKOUT: { id: 'marketplace.action.purchase', defaultMessage: 'Purchase' },
  INCLUDED: { id: 'marketplace.action.included', defaultMessage: 'Included' },
};

const DOWNLOAD_LABEL: Message = { id: 'marketplace.action.download', defaultMessage: 'Download' };

export const skillActionLabel = (intl: IntlShape, skill: EntitlementResponse): string => {
  if (skillAction(skill).kind === 'download') return intl.formatMessage(DOWNLOAD_LABEL);
  if (skill.enabled) return intl.formatMessage(ACQUISITION_LABELS.INCLUDED);
  return intl.formatMessage(ACQUISITION_LABELS[skill.acquisitionType]);
};
